import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarHeart } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Manicure",
  "Pedicure",
  "Gel / Acrylic Nails",
  "Nail Art & Custom Design",
  "Not sure yet",
];

const TIME_OPTIONS = ["10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00"];

const EMPTY_FORM = { name: "", phone: "", service_type: "", preferred_date: "", preferred_time: "", notes: "" };

export const BookingForm = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        name: form.name,
        phone: form.phone,
        service_type: form.service_type,
        preferred_date: form.preferred_date || null,
        preferred_time: form.preferred_time || null,
        notes: form.notes || null,
      });
      toast.success("Thank you! Your booking request has been sent — we'll be in touch shortly to confirm.");
      setForm(EMPTY_FORM);
    } catch (err) {
      toast.error("Something went wrong sending your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="booking" data-testid="booking-section" className="py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="mx-auto max-w-2xl rounded-[2rem] border border-brand-muted bg-white p-8 shadow-[0_24px_64px_rgba(45,27,34,0.08)] md:p-12">
          <div className="mb-10 text-center">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-brand-ground">
              <CalendarHeart className="h-6 w-6 text-brand-accent" />
            </div>
            <h2 className="font-display text-4xl font-medium tracking-tight text-brand-ink md:text-5xl">
              Book your visit
            </h2>
            <p className="mt-4 text-base leading-relaxed text-brand-ink/70">
              Send us a quick request and we&rsquo;ll get back to you to confirm your appointment.
            </p>
          </div>
          <form onSubmit={handleSubmit} data-testid="booking-form" className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="booking-name">Name</Label>
                <Input
                  id="booking-name"
                  data-testid="booking-name-input"
                  required
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your name"
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="booking-phone">Phone</Label>
                <Input
                  id="booking-phone"
                  data-testid="booking-phone-input"
                  required
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="Your phone number"
                  className="rounded-xl"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Service</Label>
              <Select
                required
                value={form.service_type}
                onValueChange={(v) => setForm((f) => ({ ...f, service_type: v }))}
              >
                <SelectTrigger data-testid="booking-service-select" className="rounded-xl">
                  <SelectValue placeholder="Choose a service" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`booking-service-option-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="booking-date">Preferred date</Label>
                <Input
                  id="booking-date"
                  data-testid="booking-date-input"
                  type="date"
                  value={form.preferred_date}
                  onChange={update("preferred_date")}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Preferred time</Label>
                <Select
                  value={form.preferred_time}
                  onValueChange={(v) => setForm((f) => ({ ...f, preferred_time: v }))}
                >
                  <SelectTrigger data-testid="booking-time-select" className="rounded-xl">
                    <SelectValue placeholder="Choose a time" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_OPTIONS.map((t) => (
                      <SelectItem key={t} value={t} data-testid={`booking-time-option-${t.replace(":", "-")}`}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="booking-notes">Notes</Label>
              <Textarea
                id="booking-notes"
                data-testid="booking-notes-input"
                value={form.notes}
                onChange={update("notes")}
                placeholder="Anything we should know? Inspiration, occasion, colours..."
                className="min-h-[110px] rounded-xl"
              />
            </div>
            <button
              type="submit"
              data-testid="booking-submit-button"
              disabled={submitting}
              className="w-full rounded-full bg-brand-accent py-4 text-sm font-medium text-white shadow-[0_8px_32px_rgba(190,24,93,0.3)] transition-transform duration-300 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
            >
              {submitting ? "Sending..." : "Send booking request"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};
